/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'

import { matchProduct } from 'views/DigitalTrayMapping/AssignedDigitalTrays/TrayProducts/TrayProduct/ProductData/ProductData'
import { useSPDScan } from '../Scan/Scan.context'
import { useSPD } from '../SPD.context'

import { BetterIDTrayScrew, TrayPlate } from '../SPD.types'
import { TrayType } from 'components/organisms/EditTrayModal/EditTrayModal.types'

import screwCaddy from './screwCaddy.json'
import upperFace from './upperFace.json'
import middleFace from './middleFace.json'
import {
  PlateCounts,
  PlateKey,
  TrayMapProps,
  plateUDIMap,
} from './TrayMap.types'
import { enqueueSnackbar } from 'notistack'
import { ScrewAnnotation } from 'components/organisms/GrayScale/GrayScale.types'

const useTrayMapLogic = ({
  trayType = 'stryker screw caddy',
  loadWithUDIs = true,
  mapContent,
}: TrayMapProps = {}) => {
  const {
    trayScrews,
    setTrayScrews,
    trayPlates,
    setTrayPlates,
    setSelectedScrew,
    selectedScrews,
    setSelectedScrews,
    setScrewLoadModalOpen,
    isLoadingWithUDIs,
    setIsLoadingWithUDIs,
    setActiveStep,
    onlyUseManualWorkflow,
    selectedScrewIndices,
    setSelectedScrewIndices,
  } = useSPD()
  const {
    setScannedCode,
    setManualCode,
    productData,
    setProductData,
    isAdditionalDeviceCountModalOpen,
    setIsAdditionalDeviceCountModalOpen,
    isLoadMultipleBoxVisible,
    setIsLoadMultipleBoxVisible,
  } = useSPDScan()

  const [confirmChangesModalOpen, setConfirmChangesModalOpen] = useState(false)
  const [isConfirmScrewRemovalModalOpen, setIsConfirmScrewRemovalModalOpen] =
    useState(false)
  const [plateCounts, setPlateCounts] = useState<PlateCounts>({})

  useEffect(() => {
    if (!loadWithUDIs) {
      setIsLoadingWithUDIs(false)
    }
  }, [loadWithUDIs])

  useEffect(() => {
    if (mapContent) {
      setTrayScrews(mapContent.screws || [])
      setTrayPlates(mapContent.plates || [])
    }
  }, [mapContent?.screws?.length, mapContent?.plates?.length])

  const getSelectedJSONFile = (trayType: TrayType) => {
    switch (trayType?.toLowerCase()) {
      case 'stryker screw caddy':
        return screwCaddy
      case 'stryker middle face':
        return middleFace
      case 'stryker upper face':
        return upperFace
      default:
        return screwCaddy
    }
  }

  const isScrewLoaded = (label: string, row: number, column: number) => {
    return trayScrews.some(
      (screw: any) =>
        screw.label === label && screw.row === row && screw.x === column
    )
  }

  const findScrew = (label: string, row: number, column: number) => {
    return trayScrews.find(
      (screw: any) =>
        screw.label === label && screw.row === row && screw.x === column
    )
  }

  const trayJSONStructure = getSelectedJSONFile(trayType)
  const screwAreas = trayJSONStructure.regions[0].areas

  const handleLoadingWithUDIs = (
    newSelectedScrew: ScrewAnnotation,
    screwIndex: number
  ) => {
    if (isLoadMultipleBoxVisible) {
      if (selectedScrewIndices.includes(screwIndex)) {
        removeSelectedScrew(newSelectedScrew)
        return
      }

      if (hasExceededScrewLimit()) {
        enqueueSnackbar("You've already exceeded your screw limit", {
          variant: 'error',
        })
        return
      }

      addSelectedScrew(screwIndex)
      return
    }
  }

  const removeSelectedScrew = (screw: ScrewAnnotation) => {
    const updatedSelectedScrews = selectedScrews.filter(
      (selectedScrew) =>
        !(
          selectedScrew.label === screw.label &&
          selectedScrew.row === screw.row_index &&
          selectedScrew.column === screw.column_label &&
          selectedScrew.x === screw.column_index
        )
    )

    setSelectedScrews(updatedSelectedScrews)
  }

  const hasExceededScrewLimit = (): boolean => {
    return selectedScrews.length === productData[0]?.deviceCount
  }

  const addSelectedScrew = (screwIndex: number) => {
    setSelectedScrewIndices((prevSelectedScrewIndices) => [
      ...prevSelectedScrewIndices,
      screwIndex,
    ])
  }

  const handleLoadScrew = (screwAsset?: BetterIDTrayScrew) => {
    setActiveStep(2)

    if (!screwAsset) return

    if (productData[0].deviceCount > 1) {
      setSelectedScrew(screwAsset)
      setIsAdditionalDeviceCountModalOpen(true)
    } else {
      handleStatusChange(screwAsset)
    }
  }

  const handleConfirmLoadMultiple = () => {
    setIsLoadMultipleBoxVisible(true)
    setIsAdditionalDeviceCountModalOpen(false)
    setSelectedScrew(null)
    setScrewLoadModalOpen(false)
  }

  const handleStatusChange = (selectedAsset: any) => {
    if (selectedAsset.type === 'plate' || selectedAsset.type === 'drill') {
      const existingPlateIndex = trayPlates.findIndex(
        (plate) => plate.plateName === selectedAsset.plateName
      )

      if (existingPlateIndex !== -1) {
        const updatedPlates = [...trayPlates]
        updatedPlates[existingPlateIndex] = {
          ...updatedPlates[existingPlateIndex],
          plateCount: updatedPlates[existingPlateIndex].plateCount + 1,
        }
        setTrayPlates(updatedPlates)
      } else {
        const updatedPlate = {
          deviceId: selectedAsset.deviceId,
          plateName: selectedAsset.plateName,
          plateCount: 1,
        }
        setTrayPlates((prev) => [...prev, updatedPlate])
      }

      setScrewLoadModalOpen(false)
      setIsAdditionalDeviceCountModalOpen(false)
    } else {
      const screwData = matchProduct(
        selectedAsset?.label,
        selectedAsset?.column
      )

      const updatedScrew = createUpdatedScrew(selectedAsset, screwData)
      const updatedScrews = [...trayScrews, updatedScrew]

      setIsAdditionalDeviceCountModalOpen(false)
      setTrayScrews(updatedScrews)
      setScrewLoadModalOpen(false)
      setSelectedScrew(null)
      setSelectedScrews([])
    }
  }

  const createUpdatedScrew = (
    selectedScrewAsset?: BetterIDTrayScrew,
    screwData?: { deviceID: string; title: string }
  ): BetterIDTrayScrew => {
    if (!selectedScrewAsset) {
      return {
        label: '',
        row: 0,
        column: 0,
        x: 0,
        deviceId: '',
        deviceDescription: '',
        company: {
          name: 'Stryker',
          id: 0,
          regEmail: '',
        },
        expirationDate: null,
        wasted: false,
      }
    }

    const baseScrew = {
      label: selectedScrewAsset.label,
      row: selectedScrewAsset.row,
      column: selectedScrewAsset.column,
      x: selectedScrewAsset.x,
    }

    if (isLoadingWithUDIs && productData[0]) {
      return {
        ...baseScrew,
        deviceId: productData[0].deviceId,
        deviceDescription: productData[0].deviceDescription,
        company: productData[0].company,
        expirationDate: productData[0].expirationDate,
        wasted: false,
      }
    }

    return {
      ...baseScrew,
      deviceId: screwData?.deviceID || '',
      deviceDescription: screwData?.title || '',
      company: {
        name: 'Stryker',
        id: 0,
        regEmail: '',
      },
      expirationDate: null,
      wasted: false,
    }
  }

  const handleCancelScrewsSelection = () => {
    setSelectedScrewIndices([])
    setIsLoadMultipleBoxVisible(false)
    setProductData([])
    setManualCode('')
    setScannedCode('')
  }

  const handleConfirmScrewsSelection = () => {
    const newSelectedScrews = selectedScrews.map((screw) => ({
      ...(screw as BetterIDTrayScrew),
      deviceId: productData[0].deviceId,
      deviceDescription: productData[0].deviceDescription,
      company: productData[0].company,
      expirationDate: productData[0].expirationDate,
      wasted: false,
    }))

    setTrayScrews((prevTrayScrews) => [...prevTrayScrews, ...newSelectedScrews])
    setSelectedScrew(null)
    setSelectedScrews([])
    setScannedCode('')
    setManualCode('')
    setIsLoadMultipleBoxVisible(false)
  }

  const handleCancelSelection = () => {
    setSelectedScrew(null)
    setSelectedScrews([])
    setManualCode('')
    setScannedCode('')
    setScrewLoadModalOpen(false)
    if (onlyUseManualWorkflow) {
      setActiveStep(0)
    }
  }

  const handleConfirmScrewRemovalModalClose = () => {
    setIsConfirmScrewRemovalModalOpen(false)
    setSelectedScrew(null)
  }

  const handleRemoveScrew = (screw: BetterIDTrayScrew) => {
    const newTrayScrews = trayScrews.filter(
      (trayScrew) =>
        !(
          trayScrew.x === screw.x &&
          trayScrew.row === screw.row &&
          trayScrew.label === screw.label
        )
    )

    setTrayScrews(newTrayScrews)
    handleConfirmScrewRemovalModalClose()
  }

  const getPlateDI = (key: PlateKey): string => {
    return plateUDIMap[key]
  }

  const getInitialPlateCount = (plateName: PlateKey): number => {
    const existingPlateIndex = trayPlates.findIndex(
      (trayPlate) => trayPlate.plateName === plateName
    )

    if (existingPlateIndex !== -1) {
      return trayPlates[existingPlateIndex].plateCount
    }

    return 0
  }

  const updateTrayPlates = (
    trayPlates: TrayPlate[],
    plateName: string,
    newCount: number,
    deviceId: string
  ): TrayPlate[] => {
    const updatedPlates = [...trayPlates]

    const existingPlateIndex = updatedPlates.findIndex(
      (trayPlate) => trayPlate.plateName === plateName
    )

    if (existingPlateIndex !== -1) {
      if (newCount <= 0) {
        return updatedPlates.filter(
          (trayPlate) => trayPlate.plateName !== plateName
        )
      } else {
        updatedPlates[existingPlateIndex] = {
          ...updatedPlates[existingPlateIndex],
          plateCount: newCount,
        }
        return updatedPlates
      }
    } else {
      if (newCount > 0) {
        return updatedPlates.concat({
          deviceId,
          plateName,
          plateCount: newCount,
        })
      }
      return updatedPlates
    }
  }

  const handlePlateCountChange = (plateName: string, newCount: number) => {
    if (plateName.includes('curvedRod')) {
      return
    }

    const plateDI = getPlateDI(plateName as PlateKey)

    const updatedTrayPlates = updateTrayPlates(
      trayPlates,
      plateName,
      newCount,
      plateDI
    )

    setTrayPlates(updatedTrayPlates)
  }

  return {
    confirmChangesModalOpen,
    setConfirmChangesModalOpen,
    isConfirmScrewRemovalModalOpen,
    setIsConfirmScrewRemovalModalOpen,
    trayScrews,
    setTrayScrews,
    screwAreas,
    handleStatusChange,
    isScrewLoaded,
    handleCancelSelection,
    findScrew,
    handleConfirmScrewRemovalModalClose,
    handleRemoveScrew,
    handleLoadScrew,
    handleConfirmLoadMultiple,
    handleConfirmScrewsSelection,
    handleCancelScrewsSelection,
    isLoadingWithUDIs,
    setIsLoadingWithUDIs,
    isAdditionalDeviceCountModalOpen,
    getSelectedJSONFile,
    plateCounts,
    setPlateCounts,
    handlePlateCountChange,
    getInitialPlateCount,
    trayPlates,
    setTrayPlates,
    handleLoadingWithUDIs,
  }
}

export default useTrayMapLogic
